import {
  Component,
  DestroyRef,
  Input,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CountdownWidget, Theme } from '../../models/reader-document';

type Align = 'flex-start' | 'center' | 'flex-end';

@Component({
  selector: 'wr-countdown-widget',
  standalone: true,
  template: `
    <section class="wr-countdown-widget px-4">

        @if (showPreviewMessage()) {
          <div class="text-sm opacity-70 px-2 py-3 w-100 text-center">
            Countdown preview is not possible
          </div>
        } @else {
          <div
            class="px-10 py-8 inline-flex w-full"
            [style.justify-content]="align()"
            [style.background-color]="bgColor()"
            [style.color]="fgColor()"
            [style.border-radius.px]="cornerRadius()"
          >
            @if (expired()) {
              <div class="text-center text-base font-medium">
                {{ expiredText() }}
              </div>
            } @else {
              @for (u of displayUnits(); track u.key) {
                <div class="text-center px-2">
                  <div class="text-5xl leading-none font-semibold tabular-nums">
                    {{ u.value }}
                  </div>
                  <div class="text-xl leading-tight opacity-90">
                    {{ u.label }}
                  </div>
                </div>
              }
            }
          </div>
        }
    </section>
  `,
})
export class CountdownWidgetComponent {
    private destroyRef = inject(DestroyRef);

    private widgetSig = signal<CountdownWidget | null>(null);
    private themeSig = signal<(Theme & { isDark?: boolean }) | null>(null);

    // ticks
    private nowSig = signal<number>(Date.now());

    @Input() set json(value: CountdownWidget) {
        this.widgetSig.set(value);
    }

    @Input() set theme(value: Theme | undefined) {
        this.themeSig.set((value as any) ?? null);
    }

    widget = computed(() => this.widgetSig());

    private colors = computed(() => {
        const t = this.themeSig();
        if (!t) return null;
        return t.isDark ? t.darkColors : t.lightColors;
    });

    align = computed<Align>(() => {
        const a = this.widget()?.alignment ?? 'center';
        if (a === 'left') return 'flex-start';
        if (a === 'right') return 'flex-end';
        return 'center';
    });

    cornerRadius = computed(
        () => this.themeSig()?.cornerRadius ?? 0,
    );

    private targetMs = computed<number | null>(() => {
        const raw = this.widget()?.targetDate;
        if (!raw) return null;

        if (typeof raw === 'object') {
            const sec =
            (raw as any)._seconds ?? (raw as any).seconds;
            const ns =
            (raw as any)._nanoseconds ?? (raw as any).nanoseconds ?? 0;

            if (typeof sec === 'number') {
            return sec * 1000 + Math.floor(ns / 1_000_000);
            }
            return null;
        }

        return null;
    });

    showPreviewMessage = computed(() => {
        const w = this.widget();
        return !!w?.targetDateKey && !w?.targetDate;
    });

    bgColor = computed(() => {
        const w = this.widget();
        return (
        w?.backgroundColor ??
        this.colors()?.background ??
        '#ffffff'
        );
    });

    fgColor = computed(() => {
        const w = this.widget();
        return (
        w?.textColor ??
        this.colors()?.title ??
        this.colors()?.primary ??
        '#111827'
        );
    });

    // --- remaining time -------------------------------------------------------

    private remainingMs = computed(() => {
        const target = this.targetMs();
        if (!target) return 0;
        return Math.max(0, target - this.nowSig());
    });

    expired = computed(() => {
        const target = this.targetMs();
        if (!target) return false;
        return this.remainingMs() <= 0;
    });

    expiredText = computed(() => {
        const w = this.widget();
        return w?.expiredMessage?.trim() || 'Expired';
    });

    private labels = computed(() => {
    const custom = this.widget()?.labels ?? {};

    return {
        weeks: (custom as any).weeks ?? 'Wochen',
        days: custom.days ?? 'Tage',
        hours: custom.hours ?? 'Stunden',
        minutes: custom.minutes ?? 'Minuten',
        seconds: custom.seconds ?? 'Sekunden',
    };
    });


    private parts = computed(() => {
    const ms = this.remainingMs();

    const totalSeconds = Math.floor(ms / 1000);

    const weeks = Math.floor(totalSeconds / (7 * 86400));
    const daysTotal = Math.floor(totalSeconds / 86400);
    const days = daysTotal % 7;

    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { weeks, daysTotal, days, hours, minutes, seconds };
    });


    displayUnits = computed(() => {
        const { weeks, daysTotal, days, hours, minutes, seconds } = this.parts();
        const L = this.labels();

        const pad2 = (n: number) => String(n).padStart(2, '0');

        // >= 7 days -> weeks + days
        if (daysTotal >= 7) {
            return [
            { key: 'weeks', value: pad2(weeks), label: (L as any).weeks ?? 'Wochen' },
            { key: 'days', value: pad2(days), label: L.days },
            ];
        }

        // 1..6 days -> days + hours
        if (daysTotal >= 1) {
            return [
            { key: 'days', value: pad2(daysTotal), label: L.days },
            { key: 'hours', value: pad2(hours), label: L.hours },
            ];
        }

        // 1..23 hours -> hours + minutes + seconds
        if (hours >= 1) {
            return [
            { key: 'hours', value: pad2(hours), label: L.hours },
            { key: 'minutes', value: pad2(minutes), label: L.minutes },
            { key: 'seconds', value: pad2(seconds), label: L.seconds },
            ];
        }

        return [
        { key: 'hours', value: '00', label: L.hours },
        { key: 'minutes', value: pad2(minutes), label: L.minutes },
        { key: 'seconds', value: pad2(seconds), label: L.seconds },
        ];

    });


    constructor() {
        // Start/stop timer automatically based on whether a target date exists
        effect((onCleanup) => {
        const target = this.targetMs();
        if (!target) return;

        const id = window.setInterval(() => {
            this.nowSig.set(Date.now());
        }, 1000);

        onCleanup(() => window.clearInterval(id));
        });

        // cleanup on destroy (belt + suspenders)
        this.destroyRef.onDestroy(() => {});
    }
}
